import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import { rootApi } from 'root-api-graphql'
import { buildFilterString } from 'services/search_api/utils'

import { MEETINGS_TAG_TYPES, buildMeetingListTag, buildMeetingTag } from './constants'
import type {
  GraphQLMeetingResult,
  MeetingsGraphQLResult,
} from './types'
import { mapScopeTypeToBackendValue } from './utils'

/** Maps frontend scope (e.g. user/me) to backend values for buildFilterString; omits scopeId when "me". */
function applyScopeMapping(variables: any): any {
  const out = { ...variables }
  const rawScopeType = variables.scopeType ?? variables.scope_type
  const rawScopeId = variables.scopeId ?? variables.scope_id
  const mapped = rawScopeType ? mapScopeTypeToBackendValue(rawScopeType, rawScopeId) : null
  if (mapped) {
    out.scopeType = mapped
    if (rawScopeId === 'me' || mapped === 'my_calendly') {
      delete out.scopeId
      delete out.scope_id
    }
  }
  return out
}

const GetMeetingsCountDocument = `
  query getMeetings(
    $query: String!,
    $status: MeetingStatus,
    $statuses: [MeetingStatus!],
    $searchIn: [MeetingSearchInFields!],
    $startTimeRange: DateFilter,
    $userUuids: [String!],
    $inviteeEmails: [String!],
    $eventTypeUuids: [String!],
    $packageUuids: [ID!],
    $kinds: [MeetingKind!],
    $meetingUuid: ID,
    $role: [MeetingRole],
    $findBestCharForPassages: Boolean,
    $isOrganizer: Boolean,
    $includeNotetakerIneligibleExternalEvents: Boolean,
  ) {
    meetings_search(
      input: {
        query: $query,
        searchIn: $searchIn,
        findBestCharForPassages: $findBestCharForPassages,
        filter: {
          start_time: $startTimeRange,
          user_uuids: $userUuids,
          invitee_emails: $inviteeEmails,
          event_type_uuids: $eventTypeUuids,
          package_uuids: $packageUuids,
          kinds: $kinds,
          status: $status,
          statuses: $statuses,
          uuid: $meetingUuid,
          role: $role,
          is_organizer: $isOrganizer,
          include_notetaker_ineligible_external_events: $includeNotetakerIneligibleExternalEvents,
        }
      }
    ) {
      total
    }
  }
`

const buildGetMeetingsQuery = (variables: any) => {
  const filterString = buildFilterString(variables)

  // istanbul ignore next
  const query = `
    query getMeetings($first: Int, $before: String, $after: String) {
      meetings(input: {
        pagination: {first: $first, before: $before, after: $after}
        filter: {${filterString}}
        orderBy: {start_time: ${variables.order || 'ASC'}}
      }) {
        total
        edges {
          node {
            uuid
            owner_user_uuid
            kind
            name
            cancelled
            status
            start_time
            end_time
            location
            location_kind
            ... on CalendlyMeeting {
              invitees_limit
              invitees_count
              event_type {
                color
                kind
              }
            }
            meeting_intelligence {
              uuid
              status
              error
            }
            attendees {
              ... on InviteeAttendee {
                cancelled
                uuid
                rsvp
              }
              ... on HostAttendee {
                uuid
              }
              ... on CoHostAttendee {
                uuid
              }
              kind
              name
              email
              timezone
            }
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  `

  return query
}

const buildGetMeetingsFilterCountQuery = (variables: any) => {
  const filterString = buildFilterString(variables)

  // istanbul ignore next
  return `
    query getMeetings {
      meetings(input: {
        filter: {${filterString}}
      }) {
        total
      }
    }
  `
}

/**
 * Shared meetings GraphQL API.
 * This API is used by both the meetings feature and the realtime meetings feature.
 */
export const meetingsGraphqlApi = rootApi
  .enhanceEndpoints({ addTagTypes: MEETINGS_TAG_TYPES })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, any, any>) => ({
      getSearchResultsCount: build.query({
        query: (variables: any) => ({ document: GetMeetingsCountDocument, variables }),
        providesTags: [buildMeetingListTag()],
      }),
      getMeetingsFilterCount: build.query({
        query: (variables: any) => {
          const mapped = applyScopeMapping(variables)
          return { document: buildGetMeetingsFilterCountQuery(mapped), variables: mapped }
        },
      }),
      getMeetings: build.query({
        query: (variables: any) => {
          const mapped = applyScopeMapping(variables)
          const newVariables = { ...mapped }
          if (variables.today) {
            newVariables.first = 10_000
          } else {
            newVariables.first = 30
          }
          return { document: buildGetMeetingsQuery(newVariables), variables: newVariables }
        },
        transformResponse: (response: any) => ({
          ...response,
          meetings: {
            ...response.meetings,
            edges: response.meetings.edges
              .filter((edge: any) => edge?.node != null)
              .map((edge: any) => ({ node: { ...edge.node, key: crypto.randomUUID() } }))
          }
        }),
        providesTags: (result: MeetingsGraphQLResult | undefined, error: unknown) => {
          const tags = [buildMeetingListTag()]
          if (result?.meetings?.edges) {
            result.meetings.edges.forEach((edge) => {
              if (edge?.node?.uuid) {
                tags.push(buildMeetingTag(edge.node.uuid))
              }
            })
          }
          return tags
        }
      }),
      getMeeting: build.query({
        query: (variables: any) => {
          // Organizer + notetaker-external flags are fixed in buildFilterString (not from variables).
          const mapped = applyScopeMapping(variables)
          const vars = { ...mapped, first: 1 }
          return {
            document: buildGetMeetingsQuery(vars),
            variables: vars,
          }
        },
        transformResponse: (response: any) => response.meetings.edges[0]?.node || null,
        providesTags: (result: GraphQLMeetingResult | undefined, error: unknown, args: { uuid?: string } | undefined) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.uuid) {
          // If we have a UUID in args but no result yet, still tag the individual meeting
            tags.push(buildMeetingTag(args.uuid))
          }
          return tags
        },
        // Suppress error alert when used as 404 fallback; meeting detail clears rail without toasting
        extraOptions: { handleErrors: false }
      })
    })
  })

// Export hooks with Gql suffix to distinguish from REST API hooks
export const {
  useGetSearchResultsCountQuery,
  useGetMeetingsFilterCountQuery,
  useLazyGetMeetingsFilterCountQuery,
  useGetMeetingsQuery: useGQLGetMeetingsQuery,
  useGetMeetingQuery: useGQLGetMeetingQuery,
  useLazyGetMeetingsQuery: useLazyGQLGetMeetingsQuery,
  useLazyGetMeetingQuery: useLazyGQLGetMeetingQuery
} = meetingsGraphqlApi
